// libs
import React, { useState, memo, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { useLocation, Redirect, useHistory } from "react-router-dom";
import isEmpty from "lodash/isEmpty";

// component
import AuthModal from "./AuthModal";
import DesktopAuth from "./components/AuthWithOTP/Desktop";
import MobileVerificationNudge from "shared/components/OTPVerification/Nudge";

// selectors
import {
    isLoggedIn as _isLoggedIn,
    isUserFetching as _isUserFetching,
    isCustomerFromIndia,
} from "shared/store/app/selectors";

// contexts
import AuthModalWrapperContext from "./AuthModalWrapperContext";
import { useResponsiveContext } from "shared/components/ResponsiveLoader/ResponsiveContext";

// helpers
import { hasSocialLoginError, isShowAuthRequested, getAuthRedirectUrl } from "./helpers";
import { getOTPFlowStatus } from "./helpers";
import {
    getRedirectionURLWithCurrentPage,
    getRedirectionURLWithReferer,
} from "shared/components/OTPVerification/helpers";

// constants
import { BREAKPOINTS } from "shared/styles/base";
import { AUTH_URI } from "shared/constants/uri";

// defs
interface IAuthModalWrapperProps {
    children: React.ReactNode;
    isLoggedIn: boolean;
    isUserFetching: boolean;
    isIndianUser: boolean;
    isOTPFlowEnabledForMobile: boolean;
    isOTPFlowEnabledForDesktop: boolean;
}

function AuthModalWrapper({
    children,
    isLoggedIn,
    isUserFetching,
    isIndianUser,
    isOTPFlowEnabledForMobile,
    isOTPFlowEnabledForDesktop,
}: IAuthModalWrapperProps) {
    const history = useHistory();
    const { search, pathname } = useLocation();
    const params = new URLSearchParams(search);
    const [canShowAuthModal, setShowAuthModal] = useState(false);
    const [redirectUrl, setRedirectUrl] = useState<string | undefined>();

    const { deviceWidth } = useResponsiveContext();
    const isDesktop = deviceWidth > BREAKPOINTS.DESKTOP;
    const canShowOTPFlow =
        (isDesktop ? isOTPFlowEnabledForDesktop : isOTPFlowEnabledForMobile) && isIndianUser;

    useEffect(() => {
        // wait till we fetch user api response
        if (isUserFetching) {
            return;
        }

        if (isLoggedIn === false && (isShowAuthRequested(search) || hasSocialLoginError())) {
            setShowAuthModal(true);
        }

        if (isLoggedIn === false && isShowAuthRequested(search)) {
            setRedirectUrl(getAuthRedirectUrl());
        }
    }, [isLoggedIn, isUserFetching, search]);

    const showAuthModal = useCallback(
        (URL?: string) => {
            if (isEmpty(URL) === false) {
                history.push(URL);
            } else if (
                isEmpty(params.get(AUTH_URI.PARAM_KEY)) === true &&
                isDesktop &&
                canShowOTPFlow
            ) {
                params.set(AUTH_URI.PARAM_KEY, AUTH_URI.LOGIN_OPTIONS);
                history.push(pathname + "?" + params.toString());
            }

            setShowAuthModal(true);
        },
        [params, isDesktop, canShowOTPFlow, history, pathname]
    );

    const onHideAuthModal = () => setShowAuthModal(false);

    return (
        <>
            <AuthModalWrapperContext.Provider value={{ showAuthModal }}>
                {children}
                <MobileVerificationNudge />
            </AuthModalWrapperContext.Provider>
            {canShowAuthModal && (
                <>
                    {!canShowOTPFlow && (
                        <AuthModal onHide={onHideAuthModal} redirectUrl={redirectUrl} />
                    )}
                    {!isDesktop && canShowOTPFlow && (
                        <Redirect
                            push
                            to={
                                redirectUrl
                                    ? getRedirectionURLWithReferer({
                                          url: AUTH_URI.LOGIN_OPTIONS,
                                          redirectUrl,
                                      })
                                    : getRedirectionURLWithCurrentPage({
                                          url: AUTH_URI.LOGIN_OPTIONS,
                                          search,
                                          pathname,
                                      })
                            }
                        />
                    )}
                    {isDesktop && canShowOTPFlow && (
                        <DesktopAuth onHide={onHideAuthModal} redirectUrl={redirectUrl} />
                    )}
                </>
            )}
        </>
    );
}

const mapStateToProps = (state: any) => {
    const { isOTPFlowEnabledForMobile, isOTPFlowEnabledForDesktop } = getOTPFlowStatus(state);

    return {
        isOTPFlowEnabledForMobile,
        isOTPFlowEnabledForDesktop,
        isLoggedIn: _isLoggedIn(state),
        isUserFetching: _isUserFetching(state),
        isIndianUser: isCustomerFromIndia(state),
    };
};

export default connect(mapStateToProps)(memo(AuthModalWrapper));
